import React, { useEffect, useState } from "react";
import { useTable, useSortBy, Column } from "react-table";
import { useDispatch, useSelector } from "react-redux";
import { RootState, actions } from "../../state/reducer";
import { CustomerListData, PaginatorArgs } from "../../types";
import { TableBurgerIcon } from "../../icons/tableBurger";
import Paginator from "../paginator";
import { CellSortIcon } from "../../icons/sort";
import ShortManInfo from "../ui/shortItem";
import { formatDateTimeStandard } from "../../utils";
import { defPageSize } from "../../constants/events";
import { backend } from "../../services";

interface ClientTableProps {
  columns: Column<CustomerListData>[];
  data: CustomerListData[];
  shorted?: boolean;
}

const tableGridTemplate = "1.5fr 2fr 2fr 2fr 2fr 2fr 2fr";

const UsersTable: React.FC<ClientTableProps> = ({ columns, data, shorted }) => {
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable<ClientTableProps>({ columns, data }, useSortBy);

  return (
    <div
      className="listTable"
      {...getTableProps()}
      style={{ display: "grid", gap: "10px" }}
    >
      {/* Заголовок таблицы */}
      <div
        className="tableHeading"
        style={{
          display: "grid",
          gridTemplateColumns: shorted ? "1fr 1fr 1fr" : tableGridTemplate,
        }}
      >
        {headerGroups.map((headerGroup) =>
          headerGroup.headers.map((column) => (
            <div
              {...column.getHeaderProps(column.getSortByToggleProps())}
              style={{ cursor: column.canSort ? "pointer" : "default" }}
            >
              {column.render("Header")}
              {column.Header !== "" && typeof column.Header === "string" ? (
                <CellSortIcon
                  width={8}
                  height={5}
                  classNames={
                    column.isSortedDesc
                      ? ["sortedDesc", "grayFilled"]
                      : ["grayFilled"]
                  }
                />
              ) : null}
            </div>
          ))
        )}
      </div>

      {/* Тело таблицы */}
      <div {...getTableBodyProps()} style={{ display: "grid", gap: "10px" }}>
        {rows.map((row) => {
          prepareRow(row);
          return (
            <div
              {...row.getRowProps()}
              style={{
                display: "grid",
                gridTemplateColumns: shorted
                  ? "1fr 1fr 1fr"
                  : tableGridTemplate,
                padding: "10px 0",
                borderBottom: "1px solid #ccc",
              }}
            >
              {row.cells.map((cell: any) => (
                <div {...cell.getCellProps()}>{cell.render("Cell")}</div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ClientListTable = (props: { search: string; shorted?: boolean }) => {
  const State = useSelector((state: RootState) => state);
  const dispatch = useDispatch();
  const [count, setCount] = useState<number>(defPageSize);
  const list = State.customers;
  const filters = State.customerFilters;
  const data = list;
  /* .filter((item) => {
      const searchRow = `${item.customer.first_name} ${item.customer.last_name} ${item.customer.description}`
      return searchRow.indexOf(props.search) > -1;
    }); */

  useEffect(() => {
    backend.getAllCustomersCount(filters).then((res) => {
      setCount(res.count);
    });
  }, [filters]);

  const handlePageSelect = (data: { page: number; perPage: number }) => {
    const offset = data.page * data.perPage;
    const clientFilters = State.customerFilters;
    dispatch(
      actions.customerRequestParams({
        ...clientFilters,
        offset: Number(offset),
        limit: data.perPage,
      })
    );
  };

  /* 
    {
      name: "Петров Иван",
      pinned_employ_name: "Анна Белова",
      date_created: "01-01-2020",
      closest_event: {
        date: "03-12-2024 16:00",
        action: "Запрос на изменение условий"
      },
      event_history: [
        { date: "01-12-2024 10:00", action: "Создание клиента" },
        { date: "02-12-2024 12:00", action: "Подписание договора" }
      ]
    }
    */

  const columnsShorted: Column<CustomerListData>[] = [
    {
      Header: "Контакт",
      accessor: "name",
      canSort: true,
      Cell: ({ row }: any) => {
        return (
          <a href={`/client/${row.original.customer.id}`}>
            <ShortManInfo
              name={`${row.original.customer.last_name || ""} ${
                row.original.customer.first_name
              }`}
              phone={row.original.customer.phone}
            />
          </a>
        );
      },
    },
    {
      Header: "Дата создания",
      accessor: "date_created",
      Cell: ({ row }: any) =>
        row.original.customer?.date_created && (
          <div className={`userRoleTableText`}>
            {formatDateTimeStandard(row.original.customer.date_created)}
          </div>
        ),
    },
    {
      Header: "Путь клиента",
      accessor: "event_history",
      Cell: ({ row }: any) => {
        return (
          <div className="arrayCell">
            {row.original.last_events.map((item, index) => (
              <p className="cellSub" key={`acsub${index}${item.date}`}>
                {`${formatDateTimeStandard(item.date)}-${item.name}`}
                <br />
              </p>
            ))}
          </div>
        );
      },
    },
  ];

  const columns: Column<CustomerListData>[] = [
    {
      Header: "Контакт",
      accessor: "name",
      canSort: true,
      Cell: ({ row }: any) => (
        <a href={`/client/${row.original.customer.id}`}>
          <ShortManInfo
            name={`${row.original.customer.last_name || ""} ${
              row.original.customer.first_name
            }`}
            phone={row.original.customer.phone}
          />
        </a>
      ),
    },
    {
      Header: "Событие",
      accessor: "closest_event",
      Cell: ({ row }: any) => {
        const events = row.original.last_events;
        const last_event =
          events && events.length > 0 ? events[events.length - 1] : null;
        return (
          <div className="multiRowCell">
            <p className="cellMain">
              {last_event ? formatDateTimeStandard(last_event.date) : ""}
            </p>
            <p className="cellSub">{last_event?.name || ""}</p>
          </div>
        );
      },
    },
    {
      Header: "Ответственый",
      accessor: "pinned_employ_name",
      Cell: ({ row }: any) =>
        row.original.user ? (
          <ShortManInfo
            photo={row.original.user.photoUrl}
            name={row.original.user.first_name}
            phone={row.original.user.phone}
          />
        ) : (
          <div className="userRoleTableText gray">Свободен</div>
        ),
    },
    {
      Header: "Дата создания",
      accessor: "date_created",
      Cell: ({ row }: any) =>
        row.original.customer?.date_created && (
          <div className={`userRoleTableText`}>
            {formatDateTimeStandard(row.original.customer.date_created)}
          </div>
        ),
    },
    {
      Header: "Источник",
      accessor: "source_name",
      Cell: ({ row }: any) => (
        <div className="userName">
          {row.original.customer?.source?.name || ""}
        </div>
      ),
    },
    /* {
      Header: "Дата создания",
      accessor: "date_created",
      Cell: ({ row }: any) => (
        row.original.customer?.date_created && <div className={`userRoleTableText`}>{formatDateTimeStandard(row.original.customer.date_created)}</div>
      ),
    }, */
    {
      Header: "Путь клиента",
      accessor: "event_history",
      Cell: ({ row }: any) => {
        return (
          <div className="arrayCell">
            {row.original.last_events.map((item, index) => (
              <p className="cellSub" key={`acsub${index}${item.date}`}>
                {`${formatDateTimeStandard(item.date)}-${item.name}`}
                <br />
              </p>
            ))}
          </div>
        );
      },
    },
  ];

  return (
    <div className="tableContainer">
      <UsersTable
        data={data}
        columns={props.shorted ? columnsShorted : columns}
        shorted={!!props.shorted}
      />
      <Paginator
        count={count}
        perPage={defPageSize}
        onDataChange={handlePageSelect}
      />
    </div>
  );
};

export default ClientListTable;
